import { useEffect, useMemo, useState } from "react";
import { Exercise } from "../../../../../models/Exercise";
import { Ticket } from "../../../../../models/Ticket";
import { SwimmingLesson } from "../../../../../models/SwimmingScheduleData";
import getId from "../../../../../../utils/getId";
import useCatchUpLessonsBooking from "./useCatchUpLessonsBooking";

export type CatchUpLessonsModalView = "slots" | "confirm";

interface GroupOption {
  groupId: string;
  label: string;
  referenceExercise: Exercise;
}

interface UseCatchUpLessonsModalParams {
  isOpen: boolean;
  ticket: Ticket;
  onClose: () => void;
  onBooked: () => void;
}

export default function useCatchUpLessonsModal({ isOpen, ticket, onClose, onBooked }: UseCatchUpLessonsModalParams) {
  const [view, setView] = useState<CatchUpLessonsModalView>("slots");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedLesson, setSelectedLesson] = useState<SwimmingLesson | null>(null);
  const [error, setError] = useState("");
  const { bookCatchUpLesson, isSubmitting } = useCatchUpLessonsBooking();

  const groupOptions = useMemo<GroupOption[]>(() => {
    const byGroup = new Map<string, Exercise>();
    [...(ticket.exercises || [])]
      .sort((a, b) => b.startTime - a.startTime)
      .forEach((exercise) => {
        const groupId = getId(exercise.exerciseGroup);
        if (groupId && !byGroup.has(groupId)) {
          byGroup.set(groupId, exercise);
        }
      });
    return Array.from(byGroup.entries()).map(([groupId, exercise]) => ({
      groupId,
      label: exercise.name || "Grupa zajęciowa",
      referenceExercise: exercise,
    }));
  }, [ticket.exercises]);

  useEffect(() => {
    if (isOpen) {
      setView("slots");
      setSelectedLesson(null);
      setError("");
      setSelectedGroupId(groupOptions[0]?.groupId || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const activeGroup = groupOptions.find((group) => group.groupId === selectedGroupId) || groupOptions[0];

  const referenceExercise: Exercise | null = activeGroup
    ? { ...activeGroup.referenceExercise, startTime: Date.now(), objectId: -1 }
    : null;

  const handleSelectLesson = (lesson: SwimmingLesson) => {
    setSelectedLesson(lesson);
    setError("");
    setView("confirm");
  };

  const handleBack = () => {
    setError("");
    setView("slots");
  };

  const handleConfirm = async () => {
    if (!selectedLesson) {
      return;
    }
    setError("");
    const result = await bookCatchUpLesson(ticket, selectedLesson);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    onBooked();
    onClose();
  };

  return {
    view,
    groupOptions,
    selectedGroupId: activeGroup?.groupId || "",
    setSelectedGroupId,
    referenceExercise,
    selectedLesson,
    error,
    isSubmitting,
    handleSelectLesson,
    handleBack,
    handleConfirm,
  };
}
